/**
 * User Login Operation
 * Isolated feature: Handle user authentication
 */
import { useState, useCallback } from 'react';
import { userService } from '../service';
import { useUserState } from './UserStateProvider';
import type { LoginRequest, User } from '@/shared/types';

export const useLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateLocalUser } = useUserState();

  const login = useCallback(async (credentials: LoginRequest): Promise<User | null> => {
    setLoading(true);
    setError(null);

    try {
      const user = await userService.login(credentials);
      
      // Update the user state immediately after successful login
      updateLocalUser(user);
      
      return user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [updateLocalUser]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    login,
    loading,
    error,
    clearError
  };
};
